import { addMonths } from 'date-fns'
import {
  asCurrency,
  asDate,
  asPercentage,
  getInvestment,
  getNumMonths,
  updateValuesFromFields,
  validateFields,
} from '../util'
import { useAppState } from '../Context'
import { SavingsChart } from '../components/Charts'
import { InvestmentScheduleTable } from '../components/Tables'
import { InputFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import CalculateRow from '../components/CalculateRow'

function PanelInput() {
  return <InputFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  return (
    <CalculateResults>
      <CalculateRow>
        <div className="richtext wysiwyg">
          <p>
            This calculator is designed to show you the amount of a level series of deposits, possibly combined with an
            initial balance (the initial lump sum) to reach a savings goal at some point in the future. It assumes that
            the funds grow at a level after-tax rate for the specified period of time, and that all deposits are made at
            the beginning of each period. It also assumes that the first level deposit is made on the first day.
          </p>
          <p className="font-bold">
            If you save {asCurrency(values.investment.depositValue)} {values.investment.frequency} until{' '}
            {asDate(values.investment.futureDate)} at {asPercentage(values.investment.interestRate)} interest, with the
            starting balance of {asCurrency(values.presentValue)}, at the end of that time you will have saved{' '}
            {asCurrency(values.futureValue)}.
          </p>
          <p>
            This might be the amount needed to fund a child's education, a 25th wedding anniversary trip, or your
            retirement.
          </p>
          <p>
            The Chart shows the year-by-year value in the fund created by the level deposits and the initial lump sum,
            if any, based on the information input.
          </p>
          <p>
            The Detail page shows the year-by-year earnings amount along with the cumulative earnings, based on the
            inputs.
          </p>
        </div>
      </CalculateRow>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <SavingsChart investment={values.investment} title="Savings Plan" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return <InvestmentScheduleTable investment={values.investment} />
}

export default {
  name: 'How much must I save for a major purchase?',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)
          const numMonths = getNumMonths(
            updatedValues.futureValue,
            updatedValues.depositValue,
            updatedValues.interestRate,
            updatedValues.presentValue
          )

          const today = new Date()
          const future = addMonths(today, numMonths)

          const opts = {
            presentDate: today,
            futureDate: future,
            presentValue: updatedValues.presentValue,
            depositValue: updatedValues.depositValue,
            interestRate: updatedValues.interestRate,
            frequency: 'monthly',
            keepRunningTotals: true,
          }

          const investment = getInvestment(opts)

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'investment', value: investment } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'futureValue',
      label: 'Amount That You Need to Save',
      value: 25000,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'presentValue',
      label: 'Amount You Have Now',
      value: 100,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'depositValue',
      label: 'Monthly Payment',
      value: 500,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate',
      label: 'Annual After-Tax Rate of Return',
      value: 6.9,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'taxRate',
      label: 'Your Income Tax Rate',
      value: 28,
    },
  ],
}
